import config from '../../config';
import { PredefinedMilisecondsTypeEnum } from '../enums';
import { FxTransactionResponse, PeopleAlsoTradeItem, PeopleAlsoTradeParams } from '../models/reporting/types';
import {
  AccountReferenceAndSummaryResponse,
  AccountSummaryPayloadData,
  AccountTradeHistoryPayloadData,
  FxTransactionPayloadData,
  GetAccountsAvailableCashRequest,
  GetMyAccountChartDataPayload,
  LeaderboardRequest,
  LeaderboardResponse,
  OpenPositionsPayloadData,
  OrderDetailPayloadData,
  OrderDetailResponse,
} from '../store/reporting/types';

import { CacheName, HttpMethods } from './cache/types';
import { BaseRequestService } from './BaseRequest.service';
import { IReportingService } from './types';

const API_URLS = {
  getAccountsAvailableCash: '/v2/reports-10n/get-available-cash-with-total-base-currency',
  getNetEquityComponents: '/v2/reports-10n/get-net-equity-base-currency',
  getOpenPositionsData: '/v2/reports-10n/get-open-positions',
  getAccountSummaryData: '/v2/reports-10n/get-account-summary-base-currency',
  leaderboard: '/v2/reports-ace/get-leaderboard',
  getOrderDetailData: '/v2/reports-10n/get-order-details',
  getAccountTradeHistory: '/v2/reports-10n/get-account-trade-history',
  fxTransactions: '/v2/reports-10n/get-fx-orders',
  peopleAlsoTrade: '/v2/reports-10n/get-people-also-trade',
  getBPInClosedMarket: '/v2/reports-10n/get-buying-power-oms-off',
};

export class ReportingService extends BaseRequestService implements IReportingService {
  private static instance: ReportingService;

  protected getBaseUrl(): string {
    return config.reporting.url;
  }

  private constructor() {
    super();
  }

  static getInstance(): ReportingService {
    if (!this.instance) {
      this.instance = new ReportingService();
    }
    return this.instance;
  }

  public getAccountsAvailableCash(requestBody: GetAccountsAvailableCashRequest) {
    const APIPath = API_URLS.getAccountsAvailableCash;
    return this.api.post<any>(APIPath, requestBody);
  }

  public getNetEquityComponents(requestBody: GetMyAccountChartDataPayload) {
    return this.api.post<any>(API_URLS.getNetEquityComponents, requestBody);
  }

  public getAccountSummaryData(requestBody: AccountSummaryPayloadData) {
    const APIPath = API_URLS.getAccountSummaryData;
    return this.api.post<AccountReferenceAndSummaryResponse>(APIPath, requestBody);
  }

  public getLeaderboard(request: LeaderboardRequest) {
    const APIPath = API_URLS.leaderboard;
    return this.api.post<LeaderboardResponse>(APIPath, request);
  }

  public getOrderDetailData(requestBody: OrderDetailPayloadData) {
    const APIPath = API_URLS.getOrderDetailData;
    const url = this.getBaseUrl() + APIPath;
    this.addOrUpdateCacheDefinition(
      url,
      CacheName.orderDetailData,
      HttpMethods.POST,
      PredefinedMilisecondsTypeEnum.fifteenMinutes,
      [ 'parentOrderId', 'accountReferenceId' ],
    );

    return this.api.post<OrderDetailResponse>(APIPath, requestBody);
  }

  public getOpenPositionsData(requestBody: OpenPositionsPayloadData) {
    const APIPath = API_URLS.getOpenPositionsData;
    return this.api.post<any>(APIPath, requestBody);
  }

  public getAccountTradeHistory(requestBody: AccountTradeHistoryPayloadData) {
    return this.api.post<AccountTradeHistoryPayloadData>(API_URLS.getAccountTradeHistory, requestBody);
  }

  public getFxTransactions(requestBody: FxTransactionPayloadData) {
    return this.api.post<FxTransactionResponse[]>(API_URLS.fxTransactions, requestBody);
  }

  public getPeopleAlsoTradeData(params: PeopleAlsoTradeParams) {
    const APIPath = API_URLS.peopleAlsoTrade;
    const url = this.getBaseUrl() + APIPath;
    this.addOrUpdateCacheDefinition(
      url,
      CacheName.peopleAlsoTrade,
      HttpMethods.POST,
      PredefinedMilisecondsTypeEnum.oneHour,
      [ 'accountReferenceId' ], // Only account ID is dynamic here
    );

    return this.api.post<PeopleAlsoTradeItem[]>(APIPath, params);
  }

  public getBPInClosedMarket(requestBody: { accountReferenceId: string }) {
    // eslint-disable-next-line max-len
    return this.api.post<{accountReferenceId: string, buyingPowerId: number}>(API_URLS.getBPInClosedMarket, requestBody);
  }
}
